import { motion } from "framer-motion";
import { AlertCircle } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { Card } from "@/components/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { getOrganisationQueryKey } from "@/features/organisations/api/get-organisation";
import { useUpdateOrganisation } from "@/features/organisations/api/update-organisation";
import { getMyOrganisationsQueryKey } from "@/features/select-organisation/api/get-my-organisations";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import {
  PublicOrganisation,
  ReplayCommentStatus,
} from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  ReplaySettingFormData,
  replaySettingSchema,
} from "../types/replay-setting-schema";

type ReplaySettingFormProps = {
  initialData: PublicOrganisation;
};

function apiDataToFormData({
  data,
}: {
  data?: PublicOrganisation;
}): ReplaySettingFormData {
  if (!data) {
    return {
      isCommentEnabled: false,
      commentType: ReplayCommentStatus.ALL,
    };
  }

  return {
    isCommentEnabled: data.replayCommentStatus !== ReplayCommentStatus.NONE,
    commentType: data.replayCommentStatus,
  };
}

export function ReplaySettingForm(props: ReplaySettingFormProps) {
  const { initialData } = props;

  const form = useForm<ReplaySettingFormData>({
    resolver: zodResolver(replaySettingSchema),
    defaultValues: apiDataToFormData({
      data: initialData,
    }),
  });

  const { mutateAsync: updateOrganisation } = useUpdateOrganisation({
    onSuccess: (result) => {
      form.reset(apiDataToFormData({ data: result }));
      queryClient.setQueryData(getOrganisationQueryKey(result.id), result);
      queryClient.invalidateQueries({
        queryKey: getMyOrganisationsQueryKey(),
      });
    },
  });

  async function handleUpdateReplaySetting(data: ReplaySettingFormData) {
    try {
      await updateOrganisation({
        id: initialData.id,
        organisation: {
          replayCommentStatus: data.isCommentEnabled
            ? data.commentType
            : ReplayCommentStatus.NONE,
        },
      });
    } catch (error) {
      const httpError = error as HttpError;

      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  const isCommentEnabled = form.watch("isCommentEnabled");

  useEffect(() => {
    if (!isCommentEnabled) {
      form.setValue("commentType", ReplayCommentStatus.NONE);
    }
  }, [isCommentEnabled, form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleUpdateReplaySetting)}>
        <Card>
          <Card.Header title="Default Settings" />
          <Card.Content className="space-y-4">
            <FormField
              control={form.control}
              name="isCommentEnabled"
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-row items-center">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="ml-2 mt-0">
                      <FormLabel className="text-base">Commenting</FormLabel>
                      <FormDescription>
                        All users to comment on Replays
                      </FormDescription>
                    </div>
                  </div>
                </FormItem>
              )}
            />

            <motion.div
              className="overflow-hidden"
              initial={{ opacity: 0, height: 0 }}
              animate={
                isCommentEnabled
                  ? { opacity: 1, height: "auto" }
                  : { opacity: 0, height: 0 }
              }
              transition={{ duration: 0.3 }}
            >
              <FormField
                control={form.control}
                name="commentType"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        className="flex flex-col space-y-1"
                      >
                        <FormItem className="flex space-x-3 space-y-0">
                          <FormControl>
                            <RadioGroupItem value={ReplayCommentStatus.ALL} />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="font-normal">
                              All Users
                            </FormLabel>
                            <FormDescription>
                              Allow Viewers, Users and Admins to comment on
                              Replays
                            </FormDescription>
                          </div>
                        </FormItem>
                        <FormItem className="flex space-x-3 space-y-0">
                          <FormControl>
                            <RadioGroupItem
                              value={ReplayCommentStatus.ONLY_ADMINS}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="font-normal">
                              Users & Admins
                            </FormLabel>
                            <FormDescription>
                              Viewers will not be able to comment on Replays
                            </FormDescription>
                          </div>
                        </FormItem>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </motion.div>

            {form.formState.errors.root && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unexpected error</AlertTitle>
                <AlertDescription>
                  {form.formState.errors.root.message}
                </AlertDescription>
              </Alert>
            )}
          </Card.Content>
          <motion.div
            className="overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={
              form.formState.isDirty
                ? { opacity: 1, height: "auto" }
                : { opacity: 0, height: 0 }
            }
            transition={{ duration: 0.3 }}
          >
            <Card.Footer className="flex justify-end">
              <Button
                type="submit"
                isLoading={form.formState.isSubmitting}
                isAnimated
              >
                Save
              </Button>
            </Card.Footer>
          </motion.div>
        </Card>
      </form>
    </Form>
  );
}
